// Páginas INTERNAS do modelo "Campo" (topo 18): /imoveis, ficha, /condominios, /empreendimento, /empresa, /blog,
// simulador e filial no visual do modelo (Inter em tudo, página de PAPEL creme, cartões quase-brancos de raio 20 com
// borda fina, campos e botões em PÍLULA, sobretítulo em versalete no acento, as cores do tenant). Só importa de
// campo.ts (paleta/vars das peças) — nada aqui é usado fora do gate ehModeloCampo: null ⇒ zero diff nos demais sites.
import type { CSSProperties } from 'react';
import { isMobile } from 'react-device-detect';
import { ehModeloCampo, hexParaRgba, misturarCores, paletaCampo, SANS_CAMPO, varsCampo } from './campo';
import type { PaletaCampo } from './campo';

export { ehModeloCampo, paletaCampo } from './campo';

/**
 * Vars da RAIZ de uma página interna: as --cp-* da paleta (varsCampo) + Inter na página toda + o fundo de PAPEL e
 * os tons "de página" derivados da tinta (--cp-sutil/--cp-sutil-forte) e a cápsula escura das fotos (--cp-capsula)
 * que os módulos CSS das internas leem atrás de [data-modelo='campo']. A régua (--cp-lado: coluna de --cp-largura
 * centrada, 24px de mínimo, --cp-lateral no celular) mora no CampoInterna.module.css (.interna). Fora do topo 18 ⇒ null.
 */
export const varsCampoPagina = (site: any): CSSProperties | null => {
    if (!ehModeloCampo(site)) return null;
    const p = paletaCampo(site);
    return {
        ...varsCampo(site),
        '--fonte-principal': SANS_CAMPO,
        '--cp-sutil': hexParaRgba(p.tinta, 0.05, 'rgba(35, 39, 31, ALPHA)'),
        '--cp-sutil-forte': hexParaRgba(p.tinta, 0.1, 'rgba(35, 39, 31, ALPHA)'),
        '--cp-capsula': hexParaRgba(misturarCores(p.profundo, '#000000', 0.35), 0.78, 'rgba(0, 0, 0, ALPHA)'),
        background: p.papel,
        color: p.tinta,
    } as CSSProperties;
};

/**
 * Vars do INVÓLUCRO do rodapé genérico nas páginas em que ele fica FORA da raiz .interna (/empresa, /blog, post,
 * /empreendimento): só as --cp-* do tenant, sem fundo nem cor, para a regra `.rodape` da casca vestir o "Pesquisas
 * mais populares" e conter o About1 no celular. Fora do topo 18 ⇒ null (zero diff nos demais sites).
 */
export const varsCampoRodape = (site: any): CSSProperties | null => (ehModeloCampo(site) ? varsCampo(site) : null);

/** Cores que o TSX precisa fora do CSS (menus do react-select em portal no <body>, spinner, slider). */
export type CoresInternasCampo = PaletaCampo & {
    borda: string;
    bordaForte: string;
    sutil: string;
    baseSuave: string;
    sombra: string;
};
export const coresInternasCampo = (site: any): CoresInternasCampo | null => {
    if (!ehModeloCampo(site)) return null;
    const p = paletaCampo(site);
    const linha = (a: number) => hexParaRgba(p.tinta, a, 'rgba(35, 39, 31, ALPHA)');
    return {
        ...p,
        borda: linha(0.1),
        bordaForte: linha(0.14),
        sutil: linha(0.05),
        baseSuave: misturarCores(p.cartao, p.base, 0.08),
        sombra: linha(0.28),
    };
};

/**
 * react-select na língua do modelo: campo em PÍLULA de 48px (a caixa de busca da referência: cartão quase-branco,
 * borda forte fina), foco = borda na base. Menu: painel cartão de raio 20 com a sombra alta; opção = pílula interna,
 * escolhida = fundo base-suave + 600.
 */
export const estilosSelectCampo = (c: CoresInternasCampo, painel: boolean) => ({
    control: (base: any, state: any) => ({
        ...base,
        minHeight: 48,
        borderRadius: 9999,
        background: c.cartao,
        boxShadow: 'none',
        border: `1px solid ${state.isFocused ? c.base : c.bordaForte}`,
        padding: '0 8px 0 12px',
        cursor: 'pointer',
        fontFamily: SANS_CAMPO,
        transition: 'border-color .2s ease',
        ':hover': { borderColor: state.isFocused ? c.base : c.tinta },
    }),
    valueContainer: (base: any) => ({ ...base, padding: '2px 6px' }),
    placeholder: (base: any) => ({ ...base, color: c.cinza, fontSize: painel ? 14 : 15, fontWeight: 500 }),
    singleValue: (base: any) => ({ ...base, color: c.tinta, fontSize: painel ? 14 : 15, fontWeight: 600 }),
    multiValue: (base: any) => ({ ...base, borderRadius: 9999, background: c.baseSuave }),
    multiValueLabel: (base: any) => ({ ...base, color: c.tinta, fontWeight: 600 }),
    multiValueRemove: (base: any) => ({ ...base, borderRadius: 9999, ':hover': { background: c.borda, color: c.tinta } }),
    input: (base: any) => ({ ...base, color: c.tinta }),
    container: (base: any) => ({ ...base, color: c.tinta }),
    indicatorSeparator: () => ({ display: 'none' }),
    dropdownIndicator: (base: any, state: any) => ({
        ...base,
        color: c.profundo,
        transition: 'transform .3s ease',
        transform: state.selectProps.menuIsOpen ? 'rotate(180deg)' : 'none',
        ':hover': { color: c.profundo },
    }),
    clearIndicator: (base: any) => ({ ...base, color: c.cinza, ':hover': { color: c.tinta } }),
    menu: (base: any) => ({
        ...base,
        marginTop: 6,
        backgroundColor: c.cartao,
        color: c.tinta,
        border: `1px solid ${c.borda}`,
        borderRadius: 20,
        overflow: 'hidden',
        boxShadow: `0 24px 48px -20px ${c.sombra}`,
        fontFamily: SANS_CAMPO,
        zIndex: 10000,
    }),
    menuList: (base: any) => ({ ...base, padding: 6 }),
    option: (base: any, state: any) => ({
        ...base,
        borderRadius: 9999,
        padding: '9px 14px',
        fontSize: 14,
        fontWeight: state.isSelected ? 600 : 500,
        cursor: 'pointer',
        backgroundColor: state.isSelected ? c.baseSuave : state.isFocused && !isMobile ? c.sutil : 'transparent',
        color: c.tinta,
        ':active': { backgroundColor: c.baseSuave },
    }),
    noOptionsMessage: (base: any) => ({ ...base, color: c.cinza }),
    loadingMessage: (base: any) => ({ ...base, color: c.cinza }),
});

/** "12 de outubro de 2026" sem Intl (toLocaleDateString depende do ICU → hydration mismatch). */
const MESES = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho', 'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'];
export const dataPorExtensoCampo = (valor: any): string => {
    const d = valor instanceof Date ? valor : new Date(String(valor || '').replace(' ', 'T'));
    if (Number.isNaN(d.getTime())) return '';
    return `${d.getDate()} de ${MESES[d.getMonth()]} de ${d.getFullYear()}`;
};
